import React, { useState, useEffect, useRef } from 'react';
import { safeString, safeUrl } from '@/src/utils/helpers';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with Vite
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});

const PropertyMap = ({ properties, isFavorite, favoritesState, onPropertyClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [popupUpdateTrigger, setPopupUpdateTrigger] = useState(0);

  const createPopupContent = (property) => {
    const formattedPrice = new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(property.total_assessed_value);

    const isFav = isFavorite(property.roll_number);
    return `
      <div style="max-width: 280px; font-family: Arial, sans-serif;">
        <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px;">
          <h3 style="margin: 0; font-size: 15px; color: #1a202c; font-weight: bold; flex: 1;">
            ${safeString(property.full_address, 'Unknown Address')}
          </h3>
          <button 
            onclick="window.toggleMapFavoriteAndUpdate('${property.roll_number}')"
            style="
              background: none; 
              border: none; 
              cursor: pointer; 
              padding: 4px; 
              border-radius: 50%; 
              transition: background-color 0.2s;
              color: ${isFav ? '#ef4444' : '#9ca3af'};
              margin-left: 8px;
            "
            onmouseover="this.style.backgroundColor='#f3f4f6'"
            onmouseout="this.style.backgroundColor='transparent'"
            title="${isFav ? 'Remove from favorites' : 'Add to favorites'}"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="${isFav ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        </div>
        <p style="margin: 4px 0; color: #4a5568; font-size: 13px;">
          <strong>Neighbourhood:</strong> ${property.neighbourhood_area || 'N/A'}
        </p>
        <p style="margin: 4px 0; color: #16a085; font-size: 15px; font-weight: bold;">
          ${formattedPrice}
        </p>
        <p style="margin: 4px 0; color: #4a5568; font-size: 12px;">
          Built: ${property.year_built || 'N/A'} | ${property.total_living_area || 'N/A'} sq ft
        </p>
        <div style="margin-top: 8px; border-top: 1px solid #e2e8f0; padding-top: 8px;">
          <a 
            href="#/property/${property.roll_number}"
            target="_blank"
            style="
              display: block;
              width: 100%;
              background-color: #3b82f6;
              color: white;
              border: none;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
              margin-bottom: 8px;
              transition: background-color 0.2s;
              text-align: center;
              text-decoration: none;
            "
            onmouseover="this.style.backgroundColor='#2563eb'"
            onmouseout="this.style.backgroundColor='#3b82f6'"
          >
            View Details
          </a>
        </div>
        <div style="margin-top: 6px; border-top: 1px solid #e2e8f0; padding-top: 6px;">
          <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(safeString(property.full_address, 'Unknown Address') + ', Winnipeg, MB, Canada')}" 
             target="_blank" 
             style="color: #3182ce; text-decoration: none; margin-right: 12px; font-size: 11px; font-weight: 500;">
            📍 Google Maps
          </a>
${safeUrl(property.full_address) ? `
          <a href="https://www.honestdoor.com/property/${safeUrl(property.full_address)}-winnipeg-mb" 
             target="_blank" 
             style="color: #16a085; text-decoration: none; margin-right: 12px; font-size: 11px; font-weight: 500;">
            🏠 HonestDoor
          </a>` : ''}
${property.detail_url && property.detail_url.url ? `
          <a href="${property.detail_url.url}" 
             target="_blank" 
             style="color: #7c3aed; text-decoration: none; font-size: 11px; font-weight: 500;">
            📄 City Assessment
          </a>` : ''}
        </div>
      </div>
    `;
  };

  const updateAllPopupContent = () => {
    if (markers.length > 0) {
      markers.forEach(marker => {
        const property = marker.propertyData;
        if (property) {
          const popupContent = createPopupContent(property);
          const wasOpen = marker.isPopupOpen();
          
          marker.bindPopup(popupContent, {
            maxWidth: 300,
            className: 'custom-popup'
          });
          
          if (wasOpen) {
            marker.closePopup();
            marker.openPopup();
          }
        }
      });
    }
  };

  useEffect(() => {
    window.navigateToPropertyFromMap = (rollNumber) => {
      const property = properties.find(p => p.roll_number === rollNumber);
      if (property && onPropertyClick) {
        onPropertyClick(property);
      }
    };
  }, [properties, onPropertyClick]);

  useEffect(() => {
    window.updateMapPopups = (specificPropertyId = null) => {
      if (specificPropertyId) {
        const targetMarker = markers.find(marker => 
          marker.propertyData && marker.propertyData.roll_number === specificPropertyId
        );
        if (targetMarker) {
          const popupContent = createPopupContent(targetMarker.propertyData);
          const wasOpen = targetMarker.isPopupOpen();
          
          targetMarker.bindPopup(popupContent, {
            maxWidth: 300,
            className: 'custom-popup'
          });
          
          if (wasOpen) {
            targetMarker.closePopup();
            targetMarker.openPopup();
          }
        }
      } else {
        setPopupUpdateTrigger(prev => prev + 1);
      }
    };
  }, [markers, createPopupContent]);

  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    
    const newMap = L.map(mapRef.current, {
      center: [49.8951, -97.1384],
      zoom: 11,
      zoomControl: true,
      scrollWheelZoom: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18
    }).addTo(newMap);

    newMap.whenReady(() => {
      setMapReady(true);
    });

    mapInstanceRef.current = newMap;
    setMap(newMap);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const currentMap = mapInstanceRef.current;
    
    if (!currentMap || !mapReady || properties.length === 0) {
      return;
    }

    try {
      const container = currentMap.getContainer();
      if (!container || !container.parentElement || !document.body.contains(container)) {
        console.warn('Map container not in DOM yet');
        return;
      }
    } catch (error) {
      console.error('Error accessing map container:', error);
      return;
    }
    
    markers.forEach(marker => {
      try {
        if (marker && currentMap.hasLayer(marker)) {
          currentMap.removeLayer(marker);
        }
      } catch (e) {
        console.warn('Error removing marker:', e);
      }
    });
    
    const newMarkers = [];
    const latLngs = [];

    properties.forEach((property) => {
      if (property.centroid_lat && property.centroid_lon) {
        const lat = parseFloat(property.centroid_lat);
        const lng = parseFloat(property.centroid_lon);
        const latLng = [lat, lng];

        const customIcon = L.divIcon({
          className: 'custom-marker',
          html: `
            <div style="
              background-color: #3182ce;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: 10px;
              font-weight: bold;
            ">🏠</div>
          `,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });

        try {
          const marker = L.marker(latLng, { icon: customIcon });
          
          marker.propertyData = property;
          
          const popupContent = createPopupContent(property);
          marker.bindPopup(popupContent, {
            maxWidth: 300,
            className: 'custom-popup'
          });
          
          marker.addTo(currentMap);
          
          newMarkers.push(marker);
          latLngs.push(latLng);
        } catch (error) {
          console.error('Error adding marker for property:', property.roll_number, error);
        }
      }
    });

    setMarkers(newMarkers);

    if (latLngs.length > 0 && newMarkers.length > 0) {
      try {
        const group = new L.featureGroup(newMarkers);
        currentMap.fitBounds(group.getBounds().pad(0.1));
        
        if (latLngs.length === 1) {
          currentMap.setZoom(Math.min(currentMap.getZoom(), 15));
        }
      } catch (error) {
        console.error('Error fitting bounds:', error);
      }
    }
  }, [mapReady, properties]);

  useEffect(() => {
    updateAllPopupContent();
  }, [popupUpdateTrigger, favoritesState]);

  return (
    <div 
      ref={mapRef} 
      style={{ 
        width: '100%', 
        height: '500px',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
      className="shadow-lg"
    />
  );
};

export default PropertyMap;